import React, { useState, useEffect } from "react";
import { Card, Table, Typography, Switch, Input, Space, Tag, Alert, Button, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Sourcing, SourcingStatus, EvaluationPhase } from "../interface";
import { GET_BIDS_BY_SOURCING_QUERY, UPDATE_BID_EVALUATION_MUTATION, START_TECHNICAL_EVALUATION_MUTATION, COMPLETE_TECHNICAL_EVALUATION_MUTATION } from "../queries";

const { Text, Title } = Typography;

interface ComplianceTabulationProps {
  record: Sourcing;
  onPhaseTransition?: () => void;
}

interface SupplierCompliance {
  supplierId: string;
  supplierName: string;
  compliance: boolean;
  remarks: string;
  passed: boolean;
  bidId: string;
}

export const ComplianceTabulation: React.FC<ComplianceTabulationProps> = ({ record, onPhaseTransition }) => {
  const [suppliers, setSuppliers] = useState<SupplierCompliance[]>([]);
  const [loading, setLoading] = useState(false);
  const [overallRemarks, setOverallRemarks] = useState("");
  const [isTechnicalInProgress, setIsTechnicalInProgress] = useState(false);
  const [isTechnicalCompleted, setIsTechnicalCompleted] = useState(false);

  const { mutate: updateBidEvaluation } = useCustomMutation();
  const { mutate: startTechnicalEvaluation } = useCustomMutation();
  const { mutate: completeTechnicalEvaluation } = useCustomMutation();

  // Query for bids
  const { refetch: refetchBids } = useCustom({
    url: '',
    method: 'get',
    meta: {
      gqlQuery: GET_BIDS_BY_SOURCING_QUERY,
      gqlVariables: {
        sourcingId: record?.id || '',
      },
    },
    queryOptions: {
      enabled: !!record?.id && record.status === SourcingStatus.CLOSED,
    },
  });

  useEffect(() => {
    if (record?.status === SourcingStatus.CLOSED && record?.id) {
      loadBidsData();
    }
  }, [record?.status, record?.id]);

  useEffect(() => {
    // Check current evaluation phase
    const phase = record.evaluationPhase;
    setIsTechnicalInProgress(phase === EvaluationPhase.TECHNICAL_IN_PROGRESS);
    setIsTechnicalCompleted(
      phase === EvaluationPhase.TECHNICAL_COMPLETED ||
      phase === EvaluationPhase.FINANCIAL_IN_PROGRESS ||
      phase === EvaluationPhase.FINANCIAL_COMPLETED ||
      phase === EvaluationPhase.COMPLETE
    );
  }, [record.evaluationPhase]);

  const loadBidsData = async () => {
    if (!record?.id || !refetchBids) return;

    setLoading(true);
    try {
      const response = await refetchBids();
      const bidsData = (response as any)?.data?.data?.getBidsBySourcing || [];

      // Transform bids data to supplier compliance format
      const suppliersData: SupplierCompliance[] = bidsData.map((bid: any) => ({
        supplierId: bid.supplierId,
        supplierName: bid.companyName || 'Unknown Supplier',
        compliance: bid.compliance !== null && bid.compliance !== undefined ? Boolean(bid.compliance) : true,
        remarks: bid.evaluationRemarks || '',
        passed: bid.technicalScore >= (record.technicalEvaluationThreshold || 0),
        bidId: bid._id,
      }));

      setSuppliers(suppliersData);
      setOverallRemarks(record.notes || '');
    } catch (error) {
      console.error('Error loading bids data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplianceChange = (supplierId: string, compliance: boolean) => {
    const updatedSuppliers = suppliers.map(supplier =>
      supplier.supplierId === supplierId ? { ...supplier, compliance } : supplier
    );
    setSuppliers(updatedSuppliers);
  };

  const handleRemarksChange = (supplierId: string, remarks: string) => {
    const updatedSuppliers = suppliers.map(supplier =>
      supplier.supplierId === supplierId ? { ...supplier, remarks } : supplier
    );
    setSuppliers(updatedSuppliers);
  };

  const handleOverallRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOverallRemarks(e.target.value);
  };

  const saveSupplierEvaluation = (supplier: SupplierCompliance) => {
    const payload = {
      input: {
        bidId: supplier.bidId,
        compliance: supplier.compliance,
        decision: supplier.passed ? 'Qualified' : 'Disqualified',
        evaluationRemarks: supplier.remarks,
      },
    };

    updateBidEvaluation(
      {
        url: "",
        method: "post",
        values: payload,
        meta: { gqlMutation: UPDATE_BID_EVALUATION_MUTATION },
      },
      {
        onSuccess: () => {
          message.success('Supplier evaluation saved successfully');
        },
        onError: (error: any) => {
          console.error('Save evaluation error:', error);
          message.error('Failed to save evaluation');
        },
      },
    );
  };

  const handleStartTechnicalEvaluation = () => {
    startTechnicalEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
          },
        },
        meta: { gqlMutation: START_TECHNICAL_EVALUATION_MUTATION },
      },
      {
        onSuccess: (data: any) => {
          message.success('Technical evaluation started');
          setIsTechnicalInProgress(true);
          if (onPhaseTransition) onPhaseTransition();
        },
        onError: (error: any) => {
          console.error('Start technical evaluation error:', error);
          message.error('Failed to start technical evaluation');
        },
      },
    );
  };

  const handleCompleteTechnicalEvaluation = () => {
    // Validate that all suppliers have compliance set
    const incompleteSuppliers = suppliers.filter(supplier => supplier.remarks === '' && !supplier.compliance);
    if (incompleteSuppliers.length > 0) {
      message.warning('Please provide remarks for all non-compliant suppliers');
      return;
    }

    completeTechnicalEvaluation(
      {
        url: "",
        method: "post",
        values: {
          input: {
            sourcingId: record.id,
          },
        },
        meta: { gqlMutation: COMPLETE_TECHNICAL_EVALUATION_MUTATION },
      },
      {
        onSuccess: (data: any) => {
          message.success('Technical evaluation completed');
          setIsTechnicalCompleted(true);
          setIsTechnicalInProgress(false);
          if (onPhaseTransition) onPhaseTransition();
        },
        onError: (error: any) => {
          console.error('Complete technical evaluation error:', error);
          message.error('Failed to complete technical evaluation');
        },
      },
    );
  };

  const columns = [
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      render: (name: string, record: SupplierCompliance) => (
        <Text strong>{name}</Text>
      ),
    },
    {
      title: 'Compliance',
      dataIndex: 'compliance',
      key: 'compliance',
      render: (compliance: boolean, record: SupplierCompliance) => (
        <Switch
          checked={compliance}
          onChange={(checked) => handleComplianceChange(record.supplierId, checked)}
          disabled={!isTechnicalInProgress}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'passed',
      key: 'passed',
      render: (passed: boolean) => (
        <Tag color={passed ? 'success' : 'error'}>
          {passed ? <><CheckCircleOutlined /> Pass</> : <><CloseCircleOutlined /> Fail</>}
        </Tag>
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (remarks: string, record: SupplierCompliance) => (
        <Input.TextArea
          value={remarks}
          onChange={(e) => handleRemarksChange(record.supplierId, e.target.value)}
          onBlur={() => saveSupplierEvaluation(record)}
          placeholder="Enter remarks (required if not compliant)"
          autoSize={{ minRows: 2, maxRows: 4 }}
          disabled={!isTechnicalInProgress}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text: string, record: SupplierCompliance) => (
        <Button
          type="link"
          onClick={() => saveSupplierEvaluation(record)}
          disabled={!isTechnicalInProgress}
        >
          Save
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading compliance data...
        </div>
      </Card>
    );
  }

  if (suppliers.length === 0) {
    return (
      <Card>
        <Alert
          message="No Bids Available"
          description="No bids have been submitted for technical evaluation."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card title="Compliance Tabulation" type="inner">
        <Table
          dataSource={suppliers}
          columns={columns}
          rowKey="supplierId"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Card title="Overall Evaluation Remarks" type="inner">
        <Input.TextArea
          value={overallRemarks}
          onChange={handleOverallRemarksChange}
          placeholder="Enter overall remarks for the technical evaluation"
          autoSize={{ minRows: 3, maxRows: 6 }}
          disabled={!isTechnicalInProgress}
        />
      </Card>

      <Card type="inner">
        <Space>
          {!isTechnicalInProgress && !isTechnicalCompleted && (
            <Button
              type="primary"
              onClick={handleStartTechnicalEvaluation}
              icon={<CheckCircleOutlined />}
            >
              Start Technical Evaluation
            </Button>
          )}

          {isTechnicalInProgress && !isTechnicalCompleted && (
            <Button
              type="primary"
              onClick={handleCompleteTechnicalEvaluation}
              icon={<CheckCircleOutlined />}
            >
              Complete Technical Evaluation
            </Button>
          )}

          {isTechnicalCompleted && (
            <Alert
              message="Technical Evaluation Completed"
              description="Technical evaluation has been completed. You can now proceed to financial evaluation."
              type="success"
              showIcon
            />
          )}
        </Space>
      </Card>

      <Card title="Evaluation Legend" size="small">
        <Space direction="vertical">
          <div><Tag color="success"><CheckCircleOutlined /> Pass</Tag> - Supplier meets technical requirements</div>
          <div><Tag color="error"><CloseCircleOutlined /> Fail</Tag> - Supplier does not meet technical requirements</div>
          <div><Switch checked /> Compliance - Toggle to indicate if bid meets overall compliance</div>
          <div><ExclamationCircleOutlined style={{ color: '#faad14' }} /> Remarks - Required for non-compliant suppliers</div>
        </Space>
      </Card>
    </Space>
  );
};